import Chart from "chart.js/auto";

// Read a theme token (e.g. `--color-accent`) from the themed root element, so
// charts read correctly in both the light `martide` theme and `dark`. A missing
// token falls back to the page's text colour.
function themeColor(varName) {
  const root = getComputedStyle(document.documentElement);
  return (
    root.getPropertyValue(varName).trim() ||
    root.getPropertyValue("--color-base-content").trim() ||
    getComputedStyle(document.body).color
  );
}

// The server sends colours as token names: `--color-success`, or
// `--color-accent / 12%` for a translucent fill. Anything else passes through.
const TOKEN = /^(--[\w-]+)(?:\s*\/\s*(\d+(?:\.\d+)?)%)?$/;

function resolveColor(value) {
  const match = typeof value === "string" && value.match(TOKEN);
  if (!match) return value;

  const [, varName, alpha] = match;
  const color = themeColor(varName);
  return alpha ? `color-mix(in oklch, ${color} ${alpha}%, transparent)` : color;
}

// Resolve every `*Color` option of a dataset (a single colour or one per bar).
// Chart.js derives hover colours by parsing the base colour, which it cannot do
// for oklch() or color-mix(), so hover keeps the resolved colour unless the
// dataset sets its own.
function resolveDataset(dataset) {
  const resolved = { ...dataset };

  for (const [key, value] of Object.entries(dataset)) {
    if (key.endsWith("Color")) {
      resolved[key] = Array.isArray(value) ? value.map(resolveColor) : resolveColor(value);
    }
  }

  resolved.hoverBackgroundColor ??= resolved.backgroundColor;
  resolved.hoverBorderColor ??= resolved.borderColor;
  return resolved;
}

// Build Chart.js options that inherit their text / grid / tooltip colors from
// the current daisyUI theme. Called on every (re)render so a theme switch
// repaints with the right contrast.
function themedOptions() {
  const text = themeColor("--color-base-content");
  const grid = `color-mix(in oklch, ${text} 12%, transparent)`;

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: text } },
      tooltip: {
        backgroundColor: themeColor("--color-base-100"),
        titleColor: text,
        bodyColor: text,
        borderColor: grid,
        borderWidth: 1,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: text } },
      y: {
        beginAtZero: true,
        grid: { color: grid },
        ticks: { precision: 0, color: text },
      },
    },
  };
}

const AdminChart = {
  mounted() {
    this.chart = null;
    this.lastConfig = null;

    this.handleEvent("chart-data", ({ id, type, labels, datasets }) => {
      if (id !== this.el.dataset.chartId) return;
      this.lastConfig = { type, labels, datasets };
      this.render();
    });

    // Repaint tick/grid/legend colors when the theme changes.
    this._onTheme = () => this.render();
    this._onStorage = (e) => {
      if (e.key === "phx:theme") this._onTheme();
    };
    window.addEventListener("phx:set-theme", this._onTheme);
    window.addEventListener("storage", this._onStorage);
  },

  render() {
    if (!this.lastConfig) return;
    const { type, labels, datasets } = this.lastConfig;

    if (this.chart) this.chart.destroy();

    this.chart = new Chart(this.el, {
      type,
      data: { labels, datasets: datasets.map(resolveDataset) },
      options: themedOptions(),
    });
  },

  destroyed() {
    if (this._onTheme) window.removeEventListener("phx:set-theme", this._onTheme);
    if (this._onStorage) window.removeEventListener("storage", this._onStorage);
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
};

export default AdminChart;
